import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import api, {
  API_ERRORS,
  getApiErrorMessage,
  getApiErrorName,
  getApiErrorStatus,
} from '../../../services/api';
import {
  ICompleteProfilePayload,
  canUseDefaultSchool,
  useAuth,
} from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import { useModal } from '../../../hooks/modal';
import PrivacyPolicyModal from '../../../modals/PrivacyPolicyModal';
import { COMPLETE_PROFILE_PATH } from '../../../routes/PrivateRoute';
import {
  FuzzyLogoNewOrange,
  PencilIcon2,
  WarningIcon,
  WhatsupIcon,
} from '../../../assets/svg/SVGComponents';
import AsyncCreateSelect from '../../../components/Selects/AsyncCreateSelect';
import CheckboxUnform from '../../../components/CheckboxUnform';
import MaskedInput from '../../../components/MaskedInput';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import getValidationErrors from '../../../utils/getValidationErrors';

import {
  Container,
  Content,
  ErrorMessage,
  FieldHint,
  LogoutButton,
  Notice,
  PolicyButton,
  UserSummary,
  schoolSelectStyles,
} from './styles';

interface ICompleteProfileFormData {
  school: string;
  phone?: string;
  privacy_policy: boolean;
  cookies_policy: boolean;
}

const onlyDigits = (value?: string): string => value?.replace(/\D/g, '') ?? '';

/**
 * Tela bloqueante de conclusão de cadastro no primeiro login.
 *
 * É autossuficiente de propósito: com o perfil incompleto o backend só libera
 * `completion-status`, `GET /schools`, `POST /user-profile/complete` e
 * `DELETE /tokens`. Qualquer outra chamada responderia 403, por isso a tela
 * renderiza fora do MainLayout (ver ProfileCompletionRoute).
 */
const CompleteProfile: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const retriedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { handleModal } = useModal();
  const { completionStatus, completeProfile, refreshIdToken, signOut } =
    useAuth();

  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [accountInactive, setAccountInactive] = useState(false);
  // O AsyncCreateSelect usa `cacheOptions`; remontar é a única forma de
  // purgar uma escola que o backend passou a rejeitar.
  const [schoolsVersion, setSchoolsVersion] = useState(0);
  const [creatingSchool, setCreatingSchool] = useState(false);

  const phoneMask = useMemo(() => {
    if (!completionStatus?.phone) {
      return undefined;
    }

    const phoneNumber = parsePhoneNumberFromString(completionStatus.phone);

    return phoneNumber?.isValid() ? phoneNumber.formatNational() : undefined;
  }, [completionStatus?.phone]);

  // Escola padrão da plataforma (Fuzzy Makers), gravada no pré-registro. Só a
  // equipe interna pode continuar nela; os demais precisam escolher o colégio.
  const mustLeaveDefaultSchool =
    !!completionStatus?.is_default_school &&
    !canUseDefaultSchool(completionStatus);

  const initialData = useMemo(
    () => ({
      // Políticas ficam SEMPRE desmarcadas: é um aceite, e aceite pré-marcado
      // não tem validade, mesmo que o completion-status já venha com `true`.
      //
      // A escola padrão não pré-preenche para quem não pode mantê-la: o backend
      // recusaria e o usuário só descobriria ao enviar.
      school:
        completionStatus?.school_id && !mustLeaveDefaultSchool
          ? {
              value: completionStatus.school_id,
              label: completionStatus.school_name,
            }
          : null,
      phone: phoneMask,
    }),
    [
      completionStatus?.school_id,
      completionStatus?.school_name,
      mustLeaveDefaultSchool,
      phoneMask,
    ],
  );

  const loadSchoolsOptions = useCallback(async (inputValue: string) => {
    const response = await api.get('/schools', {
      params: { name: inputValue, page: 1, limit: 20 },
    });

    return response.data.map((school: { id: string; name: string }) => ({
      label: school.name,
      value: school.id,
    }));
  }, []);

  /**
   * Cadastra a escola que o usuário não achou na busca e JÁ a seleciona no
   * campo: o `onCreateOption` do react-select ignora o retorno da função, então
   * sem o setFieldValue a escola nasce no banco e o campo continua vazio.
   */
  const handleCreateSchool = useCallback(
    async (inputValue: string) => {
      setCreatingSchool(true);

      try {
        const response = await api.post('/schools', { name: inputValue });

        formRef.current?.setFieldValue('school', {
          value: response.data.id,
          label: response.data.name,
        });
        formRef.current?.setFieldError('school', '');
      } catch (err) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description:
            (err as any)?.response?.data?.message ??
            t('toasts.errors.school_create_fail', {
              defaultValue:
                'Não foi possível cadastrar a escola. Tente novamente.',
            }),
        });
      } finally {
        setCreatingSchool(false);
      }
    },
    [addToast, t],
  );

  const handlePolicyModal = useCallback(() => {
    handleModal(<PrivacyPolicyModal />, {
      pathname: COMPLETE_PROFILE_PATH,
      state: {},
    });
  }, [handleModal]);

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate('/', { replace: true });
  }, [navigate, signOut]);

  /**
   * Envia a conclusão e, em caso de token expirado, renova e tenta UMA vez.
   * O retry fica aqui (e não no handleSubmit) para não precisar de recursão.
   */
  const submitPayload = useCallback(
    async (payload: ICompleteProfilePayload) => {
      try {
        await completeProfile(payload);
      } catch (err) {
        const expired =
          getApiErrorStatus(err) === 401 ||
          getApiErrorName(err) === API_ERRORS.TOKEN_EXPIRED;

        if (!expired || retriedRef.current) {
          throw err;
        }

        retriedRef.current = true;

        const token = await refreshIdToken();

        if (!token) {
          throw err;
        }

        await completeProfile(payload);
      }
    },
    [completeProfile, refreshIdToken],
  );

  const handleSubmit = useCallback(
    async (data: ICompleteProfileFormData) => {
      formRef.current?.setErrors({});
      retriedRef.current = false;
      setLoading(true);

      try {
        const schema = Yup.object().shape({
          school: Yup.string().required(
            t('yup_errors.school_required', {
              defaultValue: 'Selecione sua instituição de ensino',
            }),
          ),
          phone: Yup.string()
            .notRequired()
            .test(
              'is-valid-phone',
              t('yup_errors.invalid_phone', {
                defaultValue: 'Número de telefone inválido',
              }),
              value => {
                const digits = onlyDigits(value);

                // A máscara devolve `(__) _____-____` mesmo sem digitação:
                // sem dígitos, o campo é considerado vazio.
                if (!digits) return true;

                return !!parsePhoneNumberFromString(digits, 'BR')?.isValid();
              },
            ),
          privacy_policy: Yup.boolean().oneOf(
            [true],
            t('yup_errors.privacy_required', {
              defaultValue: 'Você precisa aceitar a política de privacidade',
            }),
          ),
          cookies_policy: Yup.boolean().oneOf(
            [true],
            t('yup_errors.cookies_required', {
              defaultValue: 'Você precisa aceitar a política de cookies',
            }),
          ),
        });

        await schema.validate(data, { abortEarly: false });

        const digits = onlyDigits(data.phone);
        const parsedPhone = digits
          ? parsePhoneNumberFromString(digits, 'BR')
          : null;

        // O backend exige `true` explícito nas duas políticas, e preserva o
        // telefone existente quando o campo não é enviado.
        await submitPayload({
          school: data.school,
          privacy_policy: true,
          cookies_policy: true,
          phone: parsedPhone?.number ?? null,
        });

        setIsSuccess(true);

        addToast({
          type: 'success',
          expression: 'Tudo certo!',
          description: t('toasts.success.profile_completed', {
            defaultValue: 'Cadastro concluído com sucesso.',
          }),
        });

        const from = (location.state as { from?: string })?.from;

        navigate(from && from !== COMPLETE_PROFILE_PATH ? from : '/home', {
          replace: true,
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
          return;
        }

        const status = getApiErrorStatus(err);
        const name = getApiErrorName(err);

        if (status === 403 && name === API_ERRORS.USER_NOT_ACTIVE) {
          setAccountInactive(true);
          return;
        }

        if (name === API_ERRORS.DEFAULT_SCHOOL_NOT_ALLOWED) {
          formRef.current?.clearField('school');

          addToast({
            type: 'error',
            expression: 'Ops!',
            description: getApiErrorMessage(err),
          });
          return;
        }

        if (status === 404 && name === API_ERRORS.SCHOOL_NOT_FOUND) {
          formRef.current?.clearField('school');
          setSchoolsVersion(version => version + 1);

          addToast({
            type: 'error',
            expression: 'Ops!',
            description: t('toasts.errors.school_not_found', {
              defaultValue: 'Escola não encontrada. Selecione novamente.',
            }),
          });
          return;
        }

        if (status === 401 || name === API_ERRORS.TOKEN_EXPIRED) {
          addToast({
            type: 'error',
            expression: 'Sua sessão expirou!',
            description: 'Por favor, faça login novamente.',
          });

          await handleLogout();
          return;
        }

        if (status === 400) {
          addToast({
            type: 'error',
            expression: 'Ops!',
            description:
              (err as any)?.response?.data?.message ??
              t('toasts.errors.profile_completion_fail', {
                defaultValue:
                  'Não foi possível concluir o cadastro. Confira os dados e tente novamente.',
              }),
          });
          return;
        }

        addToast({
          type: 'error',
          expression: 'Ops!',
          description: t('toasts.errors.profile_completion_fail', {
            defaultValue:
              'Não foi possível concluir o cadastro. Confira os dados e tente novamente.',
          }),
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, handleLogout, location.state, navigate, submitPayload, t],
  );

  if (accountInactive) {
    return (
      <Container>
        <Content>
          <FuzzyLogoNewOrange />
          <ErrorMessage>
            <span className="error-icon">
              <WarningIcon width="5rem" strokeWidth="10" stroke="#ff9800" />
            </span>
            <h2>
              {t('complete_profile.account_inactive_title', {
                defaultValue: 'Conta inativa',
              })}
            </h2>
            <p>
              {t('complete_profile.account_inactive_description', {
                defaultValue:
                  'Sua conta está desativada e não pode concluir o cadastro. Entre em contato com o suporte.',
              })}
            </p>
          </ErrorMessage>
          <LogoutButton type="button" onClick={handleLogout}>
            {t('complete_profile.logout', { defaultValue: 'Sair da conta' })}
          </LogoutButton>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <FuzzyLogoNewOrange />

        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
          <h1>
            {t('complete_profile.title', {
              defaultValue: 'Complete seu cadastro',
            })}
          </h1>
          <p className="description">
            {t('complete_profile.subtitle', {
              defaultValue:
                'Antes de continuar, confirme sua instituição de ensino e aceite nossas políticas.',
            })}
          </p>

          {mustLeaveDefaultSchool && (
            <Notice>
              {t('complete_profile.default_school_notice', {
                defaultValue:
                  'Sua conta está na escola padrão da plataforma (Fuzzy Makers), reservada à equipe interna. Escolha (ou cadastre) o colégio ao qual você pertence.',
              })}
            </Notice>
          )}

          {completionStatus && (
            <UserSummary>
              <strong>{completionStatus.name}</strong>
              <span>{completionStatus.email}</span>
            </UserSummary>
          )}

          <h4>
            {t('complete_profile.school_label', {
              defaultValue: 'Instituição de ensino',
            })}
          </h4>
          <AsyncCreateSelect
            key={schoolsVersion}
            name="school"
            className="schools"
            iconLeft={PencilIcon2}
            isLoading={creatingSchool}
            isDisabled={creatingSchool}
            placeholder={t('complete_profile.school_placeholder', {
              defaultValue: 'Digite para buscar sua escola',
            })}
            formatCreateLabel={(inputValue: string) =>
              t('complete_profile.school_create', {
                defaultValue: 'Cadastrar escola "{{name}}"',
                name: inputValue,
              })
            }
            onCreateOption={handleCreateSchool}
            createOptionThreshold={3}
            noOptionsMessage={({ inputValue }: { inputValue: string }) =>
              !inputValue || inputValue.length < 3
                ? t('complete_profile.school_min_chars', {
                    defaultValue: 'Digite pelo menos 3 caracteres para buscar',
                  })
                : t('complete_profile.school_not_found', {
                    defaultValue:
                      'Nenhuma escola encontrada. Digite o nome completo para cadastrá-la.',
                  })
            }
            isClearable
            styles={schoolSelectStyles}
            loadOptions={loadSchoolsOptions}
          />
          <FieldHint>
            {t('complete_profile.school_hint', {
              defaultValue:
                'Selecione o colégio ao qual você pertence. Se ele ainda não estiver na lista, digite o nome e cadastre.',
            })}
          </FieldHint>

          <h4>
            {t('complete_profile.phone_label', {
              defaultValue: 'Telefone (opcional)',
            })}
          </h4>
          <MaskedInput
            name="phone"
            placeholder={t('placeholders.phone', {
              defaultValue: '(00) 00000-0000',
            })}
            icon={WhatsupIcon}
            mask="(99) 99999-9999"
            value={undefined}
          />

          <CheckboxUnform name="privacy_policy" isBoolean>
            {t('navlinks.accept_policies_and_terms', {
              defaultValue: 'Aceito os termos de',
            })}{' '}
            <PolicyButton type="button" onClick={handlePolicyModal}>
              {t('navlinks.policies_and_terms', {
                defaultValue: 'políticas de privacidade da Fuzzy.',
              })}
            </PolicyButton>
          </CheckboxUnform>

          <CheckboxUnform name="cookies_policy" isBoolean>
            {t('complete_profile.cookies_accept', {
              defaultValue: 'Aceito a política de cookies da Fuzzy.',
            })}
          </CheckboxUnform>

          <PrimaryButton type="submit" loading={loading} success={isSuccess}>
            {t('complete_profile.submit', {
              defaultValue: 'Concluir cadastro',
            })}
          </PrimaryButton>
        </Form>

        <LogoutButton type="button" onClick={handleLogout}>
          {t('complete_profile.logout', { defaultValue: 'Sair da conta' })}
        </LogoutButton>
      </Content>
    </Container>
  );
};

export default CompleteProfile;
